<script setup lang="ts">

import {CustomerAccount, EditCustomerPayload, TableViewCustomer} from "@/models/dto";
import {computed, getCurrentInstance, ref, watch} from "vue";
import Customer from "@/services/customer";
import alert from '@/store/modules/alert'
import deepClone from "@/utils/deepClone";
import {URMRoleKey} from "@/models/dto/userRoleManagement";
import {findBestRoleForCustomerInTeam, teamRoles} from "@/utils/urmUtils";
import {AdminUserRole, AuthorizedUserRole} from "@/models/dto/UserRole";
import UserRoleSelector from "@/components/UserRoleSelector.vue";
import { phoneFormatFilter } from '@/utils/phone'

const props = defineProps<{
  existingCustomer: TableViewCustomer,
  enableUrmRoles: boolean,
  team: CustomerAccount
}>()

const submitting = ref<boolean>(false)
const customer = ref<EditCustomerPayload>({
  firstName: '',
  lastName: '',
  title: '',
  updatedRole: null
})
const currentUserRole = computed((): URMRoleKey | null  =>{
  return findBestRoleForCustomerInTeam(props.team.userRoles, props.existingCustomer.customerId, teamRoles)?.key
})
const selectedUserRole = ref<URMRoleKey | null>(currentUserRole.value)

const emits = defineEmits(['close', 'invalid'])

watch(props.existingCustomer, (newVal) => {
  customer.value = {
    firstName: newVal.firstName,
    lastName: newVal.lastName,
    title: newVal.title,
    updatedRole: null
  }
})

const userRoles = [AdminUserRole, AuthorizedUserRole]


const updateUserRole = (roleKey: URMRoleKey) => {
  selectedUserRole.value = roleKey

  if (currentUserRole.value === roleKey) {
    customer.value.updatedRole = null
  } else {
    customer.value.updatedRole = {
      customerAccountId: props.team.customerAccountId,
      userRole: roleKey,
    }
  }
}

const cancel = () => {
  emits('close')
}

const vueInstance = getCurrentInstance()

const submit = async (): Promise<void> => {
  const form = vueInstance.proxy.$refs['edit-customer-form'] as any
  if (!form.validate()) {
    emits('invalid')
    return
  }
  const payload = deepClone(customer.value)
  submitting.value = true

  try {
    await Customer.editCustomer(props.existingCustomer.customerId, payload)
    emits('close')
  } catch (e) {
    alert.add({
      text:
        'Error editing user. Please try again later. If the problem persists, contact support.',
      color: 'error',
      title: 'Error',
      dismissible: true,
    })

    emits('close')
  }

  submitting.value = false
}

const formattedPhone = computed(() => phoneFormatFilter(props.existingCustomer.phone))

</script>

<template>
  <v-form ref="edit-customer-form">
    <v-row dense>
      <v-col cols="12">
        <label>
          {{ $t('editCustomerForm.form.email.LABEL') }}
        </label>
        <v-text-field
          id="invite-customer-email-text-field"
          v-model="existingCustomer.email"
          disabled
          outlined
        />
      </v-col>
      <v-col cols="6">
        <label>
          {{ $t('editCustomerForm.form.firstName.LABEL') }}
        </label>
        <v-text-field
          id="edit-customer-first-name-text-field"
          v-model="existingCustomer.firstName"
          :placeholder="$t('editCustomerForm.form.firstName.PLACEHOLDER')"
          :rules="[
            (v) => !!v || $t('editCustomerForm.form.firstName.rules.REQUIRED'),
          ]"
          hide-details="auto"
          required
          validate-on-blur
          outlined
          disabled
        />
      </v-col>
      <v-col cols="6">
        <label>
          {{ $t('editCustomerForm.form.lastName.LABEL') }}
        </label>
        <v-text-field
          id="invite-customer-last-name-text-field"
          v-model="existingCustomer.lastName"
          :placeholder="$t('editCustomerForm.form.lastName.PLACEHOLDER')"
          :rules="[
            (v) => !!v || $t('editCustomerForm.form.lastName.rules.REQUIRED'),
          ]"
          hide-details="auto"
          required
          validate-on-blur
          outlined
          disabled
        />
      </v-col>
      <v-col cols="12">
        <label>
          {{ $t('common.PHONE_NUMBER') }}
        </label>
        <v-text-field
          id="invite-customer-phone-text-field"
          disabled
          :value="formattedPhone"
          outlined
        />
      </v-col>
      <v-col cols="12">
        <label>
          {{ $t('editCustomerForm.form.title.LABEL') }}
        </label>
        <v-text-field
          id="invite-customer-title-text-field"
          v-model="existingCustomer.title"
          :placeholder="$t('editCustomerForm.form.title.PLACEHOLDER')"
          hide-details="auto"
          validate-on-blur
          outlined
        />
      </v-col>
      <v-col cols="12" v-if="enableUrmRoles">
        <UserRoleSelector
          :model-value="selectedUserRole"
          :user-roles="userRoles"
          @update:model-value="updateUserRole"
        />
      </v-col>
    </v-row>
    <v-row>
      <v-col cols="6">
        <v-btn
          id="edit-customer-cancel"
          color="secondary"
          class="w-full"
          @click="cancel"
        >
          {{ $t('editCustomerForm.form.CANCEL') }}
        </v-btn>
      </v-col>
      <v-col cols="6">
        <v-btn
          id="edit-customer-save"
          color="primary"
          class="w-full"
          :loading="submitting"
          @click="submit"
        >
          {{ $t('editCustomerForm.form.SAVE') }}
        </v-btn>
      </v-col>
    </v-row>
  </v-form>
</template>
